<script lang="ts" context="module">
  import { BehaviorSubject, combineLatest, Observable } from "rxjs";
  import { map, tap } from "rxjs/operators";

  import type { Address } from "proto/address";
  import { profile$ } from "$lib/serv/core/auth";

  /**
   * `select` => continues to the home route '/'
   * `pick` => only adds the locaiton parameter on the current route
   */
  type ActionType = "" | "select" | "pick" | "add" | "edit";
  type ActionStatus = "" | "busy" | "success" | "fail";

  class Action {
    public type: ActionType;
    public status: ActionStatus;
    public additionalParams?: [string, any][];
    constructor(type: ActionType = "", status: ActionStatus = "") {
      this.type = type;
      this.status = status;
    }
  }

  export class Config {
    showMap = false;
		cancelBtnText = 'cancel';
		saveBtnText = 'Save';

    constructor(showMap = false, saveBtnText = "Save", cancelBtnText = "Cancel") {
      this.showMap = showMap;
      this.saveBtnText = saveBtnText;
      this.cancelBtnText = cancelBtnText;
    }
  }

  class State {
    action = new Action();
		address: Address | null = null;
		config = new Config();

    constructor(
      action = new Action(),
      address: Address | null = null,
      config = new Config()
    ) {
      this.action = action;
      this.address = address;
      this.config = config;
    }
  }

  const state$ = new BehaviorSubject(new State());

  /**
    * Provides the current Action state.
    */
  export const addressAction$: Observable<Action> = state$.asObservable().pipe(map((s) => s.action));

  /**
    * Opens the modal and shows AddressSelect.
    * @param onyAddLocationParamsToUrl If false (default) navigates to the home route '/'
    * when the address is seledted. If true, only adds the location parameter to the
    * current route.
    */
  export const selectAddress = (onyAddLocationParamsToUrl = false, additionalParams?: [string,any][]) => {
    const act = new Action(onyAddLocationParamsToUrl ? 'pick' : 'select');
    act.additionalParams = additionalParams;
    state$.next(new State(act));
  }

  /**
    * Opens the modal and shows AddressForm.
    * @param address - A plain Address.
    * @param config - A config object.
    */
  export const addAddress = (address: Address, config = new Config(true, "Save and continue", "Skip")) =>
    state$.next(new State(new Action("add"), address, config));

  /**
    * Opens the modal and shows AddressForm.
    * @param address - A plain Address.
    * @param config - A config object.
    */
  export const editAddress = (address: Address, config = new Config()) =>
    state$.next(new State(new Action("edit"), address, config));

  /**
    * Closes the modal.
    */
  export const closeAddressModal = () => {
    showStoreSelectModal.update(() => false);
    state$.next(new State())
  };

  const showSelect$: Observable<boolean> = state$.pipe(
    map((s) => s.action.type === "select" || s.action.type === "pick"),
  );

  const showForm$: Observable<boolean> = combineLatest([profile$, state$]).pipe(
    map(([user, state]) => {
      if (!user) return false;

      const { action, address } = state;
      if (!address) return false;

      // if (user?.saved_address$es.length >= 3) return false;

      const xaddr = xaddress(address);
      const saved = user.saved_addresses?.some((a) => xaddr.equals(a, "partial"));
      if (saved && action.type === "add") return false;

      return action.type === "add" || action.type === "edit";
    })
  );

  export const addressModalIsOpen$: Observable<boolean> = combineLatest([showSelect$, showForm$]).pipe(
    map(([showSelect, showForm]) => showSelect || showForm)
  );

  let pickTitle: string;
  let pickResponse: undefined | ((selection?: {type:string, location:Location}) => void);
  const showPicker = new RxWritable(false);
  export async function pickAddress(title = 'Select an address') {
    pickTitle = title;
    const p = new Promise<{type:string, location:Location} | undefined>((resolve) => {
      pickResponse = resolve;
      showPicker.set(true);
    });
    return p;
  }
  function picked(detail?: {type:string, location:Location}) {
    pickResponse?.(detail);
    pickResponse = undefined;
    showPicker.set(false);
  }
</script>

<script lang="ts">
  import type { Location } from "$lib/serv/locations";
  import type { Vertical } from "$lib/def/verticals";
  
  import { nav$, RxWritable } from "$lib/util/obs";
  import { xaddress, getCompletion } from "$lib/util/address";
  import { stores$ } from "$lib/serv/groc-landing";

  import Modal from "$lib/ui/shared/Modal.svelte";
  import AddressSelect from "$lib/ui/layout/AddressSelect.svelte";
  import AddressForm, { saveAddress, savingAddress$ } from "$lib/ui/layout/AddressForm.svelte";
  import { showStoreSelectModal } from "$lib/serv/nav";

  export let vertical: Vertical;
  export let currentURL: URL;
  export let selectedLabel: string;

  const _nav$: Observable<Address | null> = nav$.pipe(
    map((nav) => {
      if (!nav) return null;

      const from = xaddress(Object.fromEntries(nav.from?.url.searchParams.entries() || []));
      const to = xaddress(Object.fromEntries(nav.to?.url.searchParams.entries() || [])).toPlain();
      // console.log({ from: {...from, complete: AddressCompletion.get(from).value, }, to: {...to, complete: AddressCompletion.get(to).value, } });

      if (from.equals(to, "partial")) return null;

      return getCompletion(to) === "blank" || getCompletion(to) === "geo" ? null : to;
    }),
    tap((addr) => (addr ? addAddress(addr) : closeAddressModal()))
  );
  $_nav$;

  const save = () =>
    saveAddress().subscribe({
      next: (res) => {
        const { action } = $state$;
        state$.next(new State(new Action(action.type, "success")));
        closeAddressModal();
      },
      error: (err) => {
        const { action, address, config } = $state$;
        state$.next(new State(new Action(action.type, "fail"), address, config));
        console.error(err.message);
      },
    });
</script>

<!-- ADDRESS SELECT -->
{#if $showSelect$}
  {@const {type,additionalParams} = $addressAction$}
  <Modal on:close={closeAddressModal} id="addr-select">
    <span slot="header" class="headline-s bold">{$showStoreSelectModal ? 'Pick n Pay Store' : 'Select your address'}</span>
    <AddressSelect {currentURL} {selectedLabel} {stores$} onlyParams={type === 'pick'} {additionalParams} on:signin={closeAddressModal} />
  </Modal>
{/if}

<!-- ADDRESS PICK -->
{#if $showPicker}
  <Modal on:close={() => picked()} id="addr-select">
    <span slot="header" class="headline-s bold">{pickTitle}</span>
    <AddressSelect {currentURL} navOnSelect={false} on:selected={({detail}) => picked(detail)}/>
  </Modal>
{/if}

<!-- ADDRESS FORM -->
{#if $showForm$ && $state$.address}
  <Modal on:close={closeAddressModal} id="addr-form">
    <span slot="header" class="headline-m bold">Enter address detail</span>

    <AddressForm address={$state$.address} showMap={$state$.config.showMap} />

    <footer slot="footer" class="headline-s bold">
      <button class="button secondary skip" on:click={closeAddressModal} on:keydown>
        {$state$.config.cancelBtnText}
      </button>
      <button class="button" on:click={save} disabled={$savingAddress$}>
        {$state$.config.saveBtnText}
      </button>
    </footer>
  </Modal>
{/if}

<style lang="scss">
  @use "color";

  footer {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .skip {
      cursor: pointer;
      // background-color: color.$white;
      // &:hover {
      //   background-color: color.$grey-3;
      // }
    }

    button {
      &:disabled {
        background-color: color.$grey-3;
      }
    }
  }
</style>
